// ============================================================================
// SOZLAMALAR KATALOGI
//
// Har bir sozlamaning: standart qiymati, xavflilik darajasi va qachon kuchga
// kirishi shu yerda. Yangi sozlama qo'shilsa, barcha restoranlar standart
// qiymatni avtomatik oladi — har biriga qo'lda tegish shart emas.
//
// Darajalar:
//   oddiy    — ega yoki huquqi bor menejer darhol o'zgartiradi
//   muhim    — ega o'zgartiradi, ta'siri oldindan ko'rsatiladi va tasdiq so'raladi
//   tizimli  — faqat platforma admini (yoki u ruxsat bergan ega)
// ============================================================================

export type SettingLevel = 'oddiy' | 'muhim' | 'tizimli';

/** Qachon kuchga kiradi: darhol, keyingi smenadan yoki ertangi ish kunidan */
export type SettingEffect = 'immediate' | 'next_shift' | 'next_business_day';

interface BaseSetting {
  key: string;
  label: string;
  description: string;
  level: SettingLevel;
  effect: SettingEffect;
}

export type SettingDef =
  | (BaseSetting & { type: 'boolean'; default: boolean })
  | (BaseSetting & { type: 'number'; default: number; min: number; max: number })
  | (BaseSetting & { type: 'text'; default: string; maxLength: number })
  | (BaseSetting & { type: 'enum'; default: string; options: readonly { value: string; label: string }[] })
  | (BaseSetting & {
      type: 'multi';
      default: readonly string[];
      options: readonly { value: string; label: string }[];
      minItems: number;
    })
  | (BaseSetting & { type: 'time'; default: string });

export const SETTINGS = [
  {
    key: 'branch.business_day_start',
    label: 'Ish kuni boshlanishi',
    description: 'Shu soatgacha bo‘lgan savdo kechagi ish kuniga yoziladi',
    level: 'tizimli',
    effect: 'next_business_day',
    type: 'time',
    default: '06:00',
  },
  {
    key: 'branch.cash_rounding',
    label: 'Naqd pulni yaxlitlash',
    description: 'Naqd to‘lovda jami summa shu qadamgacha yaxlitlanadi',
    level: 'muhim',
    effect: 'next_shift',
    type: 'enum',
    default: '0',
    options: [
      { value: '0', label: 'Yaxlitlanmaydi' },
      { value: '100', label: '100 so‘mgacha' },
      { value: '500', label: '500 so‘mgacha' },
      { value: '1000', label: '1000 so‘mgacha' },
    ],
  },
  {
    key: 'flow.payment_timing',
    label: 'To‘lov qachon olinadi',
    description: 'Avval (fastfood) yoki ovqatdan keyin (restoran)',
    level: 'tizimli',
    effect: 'next_shift',
    type: 'enum',
    default: 'before',
    options: [
      { value: 'before', label: 'Avval' },
      { value: 'after', label: 'Ovqatdan keyin' },
      { value: 'by_order_type', label: 'Buyurtma turiga qarab' },
    ],
  },
  {
    key: 'flow.order_taker',
    label: 'Buyurtmani kim oladi',
    description: 'Kassir, ofitsiant yoki ikkalasi',
    level: 'tizimli',
    effect: 'next_shift',
    type: 'enum',
    default: 'cashier',
    options: [
      { value: 'cashier', label: 'Kassir' },
      { value: 'waiter', label: 'Ofitsiant' },
      { value: 'both', label: 'Ikkalasi' },
    ],
  },
  {
    key: 'flow.kitchen_routing',
    label: 'Buyurtma oshxonaga qanday boradi',
    description: 'Og‘zaki, ekranga, printerga yoki ikkalasiga',
    level: 'tizimli',
    effect: 'next_shift',
    type: 'enum',
    default: 'none',
    options: [
      { value: 'none', label: 'Og‘zaki (tizimsiz)' },
      { value: 'screen', label: 'Oshxona ekrani' },
      { value: 'printer', label: 'Oshxona printeri' },
      { value: 'screen_and_printer', label: 'Ekran va printer' },
    ],
  },
  {
    key: 'flow.order_types',
    label: 'Buyurtma turlari',
    description: 'Restoranda qaysi buyurtma turlari bor',
    level: 'muhim',
    effect: 'next_shift',
    type: 'multi',
    default: ['takeaway'],
    minItems: 1,
    options: [
      { value: 'dine_in', label: 'Zalda' },
      { value: 'takeaway', label: 'Olib ketish' },
      { value: 'delivery', label: 'Yetkazib berish' },
    ],
  },
  {
    key: 'flow.service_charge_percent',
    label: 'Xizmat haqi, %',
    description: 'Zaldagi buyurtmalarga qo‘shiladi',
    level: 'muhim',
    effect: 'next_shift',
    type: 'number',
    default: 0,
    min: 0,
    max: 30,
  },
  {
    key: 'pos.ask_order_type',
    label: 'Buyurtma turini so‘rash',
    description: 'Har buyurtmada “zal yoki olib ketish” so‘raladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'pos.top_sellers',
    label: '“Ko‘p sotilganlar” bo‘limi',
    description: 'Kassada birinchi bo‘lim: shu filialda oxirgi 14 kunda eng ko‘p sotilgan 12 ta mahsulot',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'pos.category_images',
    label: 'Kategoriya tugmalarida fon rasmi',
    description: 'Kassada kategoriya tugmasi fonida rasm (kategoriyaga rasm qo‘yilgan bo‘lsa). O‘chirilsa — oddiy tugma, mahsulot rasmlari qoladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'stock.price_over',
    label: 'Kirim narxi chegaradan oshsa',
    description: 'Xom ashyoga qo‘yilgan eng yuqori narxdan qimmat kirim: rad etiladi yoki saqlanadi, lekin narxi siz tasdiqlaguncha tannarxga kirmaydi',
    level: 'muhim',
    effect: 'immediate',
    type: 'enum',
    default: 'reject',
    options: [
      { value: 'reject', label: 'Rad etilsin' },
      { value: 'approve', label: 'Saqlansin, ega tasdiqlasin' },
    ],
  },
  {
    key: 'stock.suspicious_pct',
    label: 'Shubhali kirim chegarasi',
    description: 'Narx oxirgi kirimdan yoki o‘rtacha tannarxdan shuncha foiz qimmat bo‘lsa — kirim “shubhali” deb belgilanadi',
    level: 'muhim',
    effect: 'immediate',
    type: 'enum',
    default: '30',
    options: [
      { value: '0', label: 'O‘chiq' },
      { value: '20', label: '20%' },
      { value: '30', label: '30%' },
      { value: '50', label: '50%' },
    ],
  },
  {
    key: 'pos.pay_methods',
    label: 'To‘lov turlari',
    description: 'Kassada qaysi to‘lov turlari chiqadi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'multi',
    default: ['cash', 'card', 'click', 'payme', 'uzum', 'transfer'],
    minItems: 1,
    options: [
      { value: 'cash', label: 'Naqd' },
      { value: 'card', label: 'Karta' },
      { value: 'click', label: 'Click' },
      { value: 'payme', label: 'Payme' },
      { value: 'uzum', label: 'Uzum' },
      { value: 'transfer', label: 'O‘tkazma' },
    ],
  },
  {
    key: 'receipt.header',
    label: 'Chek sarlavhasi',
    description: 'Chek tepasidagi matn (manzil, telefon)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: '',
    maxLength: 200,
  },
  {
    key: 'receipt.footer',
    label: 'Chek osti',
    description: 'Chek pastidagi matn (rahmat so‘zi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: 'Xaridingiz uchun rahmat!',
    maxLength: 200,
  },
  {
    key: 'receipt.brand',
    label: 'Chek oxirida ONE POS imzosi',
    description: 'Chek eng pastida kichik qator: “ONE POS — onepos.uz”',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.top',
    label: 'Chek tepasidagi xabar',
    description: 'Restoran nomidan oldin, masalan: “Xush kelibsiz!”',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: '',
    maxLength: 120,
  },
  {
    key: 'receipt.name_size',
    label: 'Restoran nomi kattaligi',
    description: 'Chekdagi restoran nomi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '2',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.name_bold',
    label: 'Restoran nomi qalin',
    description: 'Chekdagi restoran nomi qalin harflarda',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.logo',
    label: 'Chek tepasida logotip',
    description: 'Oq-qora rasm (Sozlamalar → Chek sahifasida tayyorlanadi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: '',
    maxLength: 26000,
  },
  {
    key: 'receipt.show_logo',
    label: 'Chekda logotip',
    description: 'Logotip tayyorlangan bo‘lsa — chek tepasida chiqadi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.logo_size',
    label: 'Chekdagi logotip kattaligi',
    description: 'Qog‘oz enining qanchasini egallaydi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: 'M',
    options: [
      { value: 'S', label: 'Kichik' },
      { value: 'M', label: 'O‘rta' },
      { value: 'L', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.top_icon',
    label: 'Tepadagi xabar yonidagi rasm',
    description: 'Oq-qora kichik rasm (Sozlamalar → Chek sahifasida tayyorlanadi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: '',
    maxLength: 8000,
  },
  {
    key: 'receipt.top_icon_size',
    label: 'Tepadagi xabar yonidagi rasm kattaligi, %',
    description: 'Yozuv bo‘yiga nisbatan: 100 — yozuv bilan bir xil',
    level: 'oddiy',
    effect: 'immediate',
    type: 'number',
    default: 100,
    min: 50,
    max: 250,
  },
  {
    key: 'receipt.header_icon',
    label: 'Nomdan keyingi matn yonidagi rasm',
    description: 'Oq-qora kichik rasm (Sozlamalar → Chek sahifasida tayyorlanadi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'text',
    default: '',
    maxLength: 8000,
  },
  {
    key: 'receipt.header_icon_size',
    label: 'Matn yonidagi rasm kattaligi, %',
    description: 'Yozuv bo‘yiga nisbatan: 100 — yozuv bilan bir xil',
    level: 'oddiy',
    effect: 'immediate',
    type: 'number',
    default: 100,
    min: 50,
    max: 250,
  },
  {
    key: 'receipt.show_contacts',
    label: 'Chekda manzil',
    description: 'Filial manzili (Sozlamalar → Chek sahifasida kiritiladi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.show_phone',
    label: 'Chekda telefon',
    description: 'Filial telefoni (Sozlamalar → Chek sahifasida kiritiladi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.show_no',
    label: 'Chekda buyurtma raqami',
    description: 'Mijoz buyurtmasini shu raqam bilan oladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.show_time',
    label: 'Chekda sana va vaqt',
    description: 'Buyurtma berilgan vaqt',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.show_staff',
    label: 'Chekda kassir nomi',
    description: 'Buyurtmani olgan xodim',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.top_size',
    label: 'Chek: Tepadagi xabar — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.top_bold',
    label: 'Chek: Tepadagi xabar — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'receipt.contacts_size',
    label: 'Chek: Filial, manzil, telefon va nomdan keyingi matn — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.contacts_bold',
    label: 'Chek: Filial, manzil, telefon va nomdan keyingi matn — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'receipt.no_size',
    label: 'Chek: Buyurtma raqami — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '2',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.no_bold',
    label: 'Chek: Buyurtma raqami — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.info_size',
    label: 'Chek: Sana, buyurtma turi, stol, kassir — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.info_bold',
    label: 'Chek: Sana, buyurtma turi, stol, kassir — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'receipt.items_size',
    label: 'Chek: Taomlar (nomi, qo‘shimchalari, soni × narx) — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.items_bold',
    label: 'Chek: Taomlar (nomi, qo‘shimchalari, soni × narx) — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'receipt.sums_size',
    label: 'Chek: Jami, chegirma, to‘lov usullari, qaytim — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.sums_bold',
    label: 'Chek: Jami, chegirma, to‘lov usullari, qaytim — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'receipt.total_size',
    label: 'Chek: Yakuniy summa (TO‘LOV) — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '2',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.total_bold',
    label: 'Chek: Yakuniy summa (TO‘LOV) — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'receipt.footer_size',
    label: 'Chek: Chek osti — kattalik',
    description: 'Oddiy; baland (2 baravar baland, eni o‘zgarmaydi); katta (2 baravar baland va keng — qatorga yarim harf sig‘adi)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: 'Oddiy' },
      { value: '2', label: 'Baland' },
      { value: '3', label: 'Katta' },
    ],
  },
  {
    key: 'receipt.footer_bold',
    label: 'Chek: Chek osti — qalin',
    description: 'Qalin harflar — xira chiqaradigan printerda yaxshi o‘qiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'hall.long_minutes',
    label: 'Stol uzoq band — chegara, daqiqa',
    description: 'Stol shundan uzoq band bo‘lsa, kassada ajratib ko‘rsatiladi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'number',
    default: 60,
    min: 10,
    max: 600,
  },
  {
    key: 'hall.start',
    label: 'Kassa zal sxemasidan boshlanadi',
    description: 'Kassa ochilganda va har buyurtmadan keyin zal sxemasi chiqadi (stol bosilib buyurtma ochiladi). Restoran, choyxona, bar uchun',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'hall.waiter_payment',
    label: 'Ofitsiant to‘lov qabul qiladi',
    description:
      'Ofitsiant telefonidan to‘lov kiritish. “Faqat kassada” — ofitsiant buyurtma va hisob cheki bilan ishlaydi, pulni kassir oladi. “Ofitsiant ham” — to‘lov ofitsiant nomiga yoziladi va kassadagi ochiq smenaga tushadi (naqdni ofitsiant smena yopilguncha kassaga topshiradi)',
    level: 'muhim',
    effect: 'immediate',
    type: 'enum',
    default: 'kassa',
    options: [
      { value: 'kassa', label: 'Faqat kassada' },
      { value: 'waiter', label: 'Ofitsiant ham' },
    ],
  },
  {
    key: 'hall.bill_lock',
    label: 'Hisob chekidan keyin hisob qulflanadi',
    description:
      'Prechek chiqarilgach taom qo‘shib, bekor qilib yoki birlashtirib bo‘lmaydi — mehmonga ko‘rsatilgan summa o‘zgarmaydi. Kerak bo‘lsa, “Bekor qilish” huquqi bor xodim “Hisobni ochish” qiladi (jurnalga yoziladi). O‘chiq bo‘lsa — faqat “hisob o‘zgardi, qayta chiqaring” ogohlantirishi',
    level: 'muhim',
    effect: 'immediate',
    type: 'boolean',
    default: true,
  },
  {
    key: 'waiter.tables',
    label: 'Ofitsiantlar stollari',
    description:
      '“O‘z stoli” — ofitsiant faqat o‘zi ochgan stolni o‘zgartiradi, boshqalarnikini ko‘radi. “Umumiy” — har qanday ofitsiant istalgan stolga taom qo‘sha oladi (kichik kafe). “Faqat o‘zinikini ko‘radi” — boshqa ofitsiant stoli faqat “band” bo‘lib ko‘rinadi, ichidagisi va summasi yashirin. Istisno — “Boshqa ofitsiant stolini o‘zgartirish” huquqi',
    level: 'muhim',
    effect: 'immediate',
    type: 'enum',
    default: 'own',
    options: [
      { value: 'own', label: 'O‘z stoli (boshqalarnikini ko‘radi)' },
      { value: 'shared', label: 'Umumiy — har kim qo‘sha oladi' },
      { value: 'own_only', label: 'Faqat o‘zinikini ko‘radi' },
    ],
  },
  {
    key: 'hall.ask_guests',
    label: 'Mehmonlar soni',
    description: 'Stol ochilganda “Nechta mehmon?” so‘raladimi. Hisobotda o‘rtacha chek bir mehmonga hisoblanadi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: 'ask',
    options: [
      { value: 'off', label: 'So‘ralmaydi' },
      { value: 'ask', label: 'So‘raladi (o‘tkazib yuborish mumkin)' },
      { value: 'required', label: 'Majburiy' },
    ],
  },
  {
    key: 'waiter.ready_notify',
    label: '“Taom tayyor” xabari ofitsiantga',
    description:
      'Oshxona ekranida taom “tayyor” bo‘lganda shu stol ofitsiantining telefonida ovoz, tebranish va xabar; “Olib chiqdim” tugmasi. Oshxona ekrani ishlatilganda ishlaydi',
    level: 'oddiy',
    effect: 'immediate',
    type: 'boolean',
    default: false,
  },
  {
    key: 'order.void_stock',
    label: 'Bekor qilingan taom — sklad',
    description:
      'Buyurtma yoki taom bekor qilinganda mahsulot skladga qaytadimi yoki isrof bo‘ladimi. Kassir har safar ko‘radi va o‘zgartira oladi. Oshxona ekrani bo‘lmasa, taom holati noma’lum — “avtomatik” isrof deb hisoblaydi',
    level: 'muhim',
    effect: 'immediate',
    type: 'enum',
    default: 'auto',
    options: [
      { value: 'auto', label: 'Avtomatik: tayyorlanmagani qaytadi, tayyorlangani isrof' },
      { value: 'waste', label: 'Odatda isrof' },
      { value: 'return', label: 'Odatda skladga qaytadi' },
      { value: 'ask', label: 'Kassir har safar o‘zi tanlaydi' },
    ],
  },
  {
    key: 'ui.theme',
    label: 'Ko‘rinish (mavzu)',
    description: 'Restoran uchun standart ko‘rinish. Har bir xodim o‘zi uchun boshqasini tanlashi mumkin',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: 'system',
    options: [
      { value: 'system', label: 'Telefonga moslash' },
      { value: 'modern', label: 'Zamonaviy' },
      { value: 'dark', label: 'Tungi' },
      { value: 'classic', label: 'Klassik' },
      { value: 'contrast', label: 'Yuqori kontrast' },
    ],
  },
  {
    key: 'ui.accent',
    label: 'Asosiy rang',
    description: 'Tugmalar va ajratilgan joylar rangi (butun restoran uchun)',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: 'amber',
    options: [
      { value: 'amber', label: 'Zafaron' },
      { value: 'blue', label: 'Ko‘k' },
      { value: 'green', label: 'Yashil' },
      { value: 'teal', label: 'Firuza' },
      { value: 'violet', label: 'Binafsha' },
      { value: 'pink', label: 'Pushti' },
      { value: 'coffee', label: 'Qahvarang' },
      { value: 'graphite', label: 'Grafit' },
    ],
  },
  // Maslahatchi (Q-075)
  {
    key: 'advisor.purchase_days',
    label: 'Xarid ro‘yxati necha kunga',
    description: 'Maslahatchi xarid ro‘yxatini shuncha kunlik sarfga hisoblaydi. Ro‘yxatning o‘zida ham almashtirish mumkin',
    level: 'oddiy',
    effect: 'immediate',
    type: 'enum',
    default: '1',
    options: [
      { value: '1', label: '1 kun' },
      { value: '2', label: '2 kun' },
      { value: '3', label: '3 kun' },
      { value: '7', label: '1 hafta' },
    ],
  },
  {
    key: 'advisor.safety_pct',
    label: 'Xaridda zaxira, %',
    description: 'Prognozdan ortiqcha olinadigan ulush — xom ashyo tugab qolmasligi uchun. 0 — aniq prognoz bo‘yicha',
    level: 'oddiy',
    effect: 'immediate',
    type: 'number',
    default: 15,
    min: 0,
    max: 100,
  },
] as const satisfies readonly SettingDef[];

export type SettingKey = (typeof SETTINGS)[number]['key'];

const SETTING_MAP: ReadonlyMap<string, SettingDef> = new Map(
  SETTINGS.map((s) => [s.key, s as SettingDef] as const),
);

export function getSetting(key: string): SettingDef | undefined {
  return SETTING_MAP.get(key);
}

/** Qiymat sozlama turiga mosmi. Xato bo'lsa — tushunarli matn, aks holda null. */
export function validateSettingValue(def: SettingDef, value: unknown): string | null {
  switch (def.type) {
    case 'boolean':
      return typeof value === 'boolean' ? null : 'Ha yoki yo‘q bo‘lishi kerak';
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'Son bo‘lishi kerak';
      if (value < def.min || value > def.max) return `${def.min} dan ${def.max} gacha bo‘lishi kerak`;
      return null;
    case 'text':
      if (typeof value !== 'string') return 'Matn bo‘lishi kerak';
      return value.length > def.maxLength ? `${def.maxLength} belgidan oshmasin` : null;
    case 'enum':
      return def.options.some((o) => o.value === value) ? null : 'Ro‘yxatdagi qiymatlardan biri bo‘lishi kerak';
    case 'multi': {
      if (!Array.isArray(value) || !value.every((v) => typeof v === 'string')) return 'Ro‘yxat bo‘lishi kerak';
      if (new Set(value).size !== value.length) return 'Takrorlanmasin';
      if (value.length < def.minItems) return `Kamida ${def.minItems} ta tanlang`;
      return value.every((v) => def.options.some((o) => o.value === v))
        ? null
        : 'Ro‘yxatdagi qiymatlardan bo‘lishi kerak';
    }
    case 'time':
      return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
        ? null
        : 'Vaqt SS:DD ko‘rinishida bo‘lishi kerak';
  }
}
